import React, { PropTypes, Component } from 'react';
import classnames from 'classnames';

/**
 * @module common/components/animated_button
 */

const initialState = { successMsg: '', errorMsg: '', timer: null };

/**
 * Animated button.
 * Serves as an augmented button with action, success and error indicators.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
class AnimatedButton extends Component {
  constructor(props) {
    super(props);
    this.state = initialState;
  }

  componentWillReceiveProps(nextProps) {
    /* processing ended: display either an error or success message for `nextProps.msgTimeout`
       milliseconds */
    if (!nextProps.processing && this.props.processing) {
      /* clear existing timer if any */
      clearTimeout(this.state.timer);

      const newState = Object.assign({}, initialState);

      if (nextProps.errorMsg) {
        newState.errorMsg = nextProps.errorMsg;
      } else {
        newState.successMsg = nextProps.successMsg;
      }

      newState.timer = setTimeout(() => this.setState(initialState), nextProps.msgTimeout || 2000);

      this.setState(newState);
    }
  }

  componentWillUnmount() {
    /* clear timer to avoid setting state in an unmounted component */
    clearTimeout(this.state.timer);
  }

  render() {
    const {
      processing, disabled, onClick, type, label, processingLabel,
      errorLabel, successLabel, baseBtnClass, background
    } = this.props;

    const { errorMsg, successMsg } = this.state;

    /* base button class */
    let btnClass = baseBtnClass || 'btn-tertiary btn-block';
    if (errorMsg) {
      btnClass = 'btn-danger';
    } else if (successMsg) {
      btnClass = 'btn-success';
    }

    /* button label */
    let btnLabel = label;
    if (processing) {
      btnLabel = processingLabel;
    } else if (errorMsg) {
      btnLabel = errorLabel || 'Whoops!';
    } else if (successMsg) {
      btnLabel = successLabel || 'Done!';
    }

    /* additional info */
    let info = '';
    let infoClasses = 'info';
    let infoIcon = '';
    if (errorMsg) {
      info = errorMsg;
      infoClasses += ' danger';
      infoIcon = 'fa fa-times-circle';
    } else if (successMsg) {
      info = successMsg;
      infoClasses += ' success';
      infoIcon = 'fa fa-check-circle';
    }

    const btnDisabled = processing || disabled || successMsg || errorMsg;
    const btnClasses = classnames("btn btn-animated", btnClass, {
      'btn-loading': processing,
    });

    return (
      <div className={"btn-animated-wrapper " + (background? `background-${background}` : '')}>
        <button className={btnClasses} onClick={onClick} disabled={btnDisabled} type={type}>
          <span className="content">{btnLabel}</span>
          <span className="progress">
            <span className="progress-inner" />
          </span>
        </button>
        { info && typeof info === 'string' ?
          <span className={infoClasses}>
            <i className={infoIcon} /> { info }
          </span> : null }
      </div>
    );
  }
}

AnimatedButton.propTypes = {
  processing: PropTypes.bool,
  msgTimeout: PropTypes.number,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  type: PropTypes.string,
  baseBtnClass: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  processingLabel: PropTypes.string,
  successLabel: PropTypes.string,
  errorLabel: PropTypes.string,
  background: PropTypes.string,
  errorMsg: PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.bool]),
  successMsg: PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.bool])
};

export default AnimatedButton;
